import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import CreatorSidebar from '../../components/feature/CreatorSidebar';
import UploadModal from '../../components/feature/UploadModal';

export default function CreatorDashboard() {
  const navigate = useNavigate();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // 사용자 인증 확인
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      const userData = JSON.parse(localStorage.getItem('currentUser') || '{}');
      setCurrentUser(userData);

      // 크리에이터가 아닌 경우 리다이렉트
      if (userData.user_type !== 'creator') {
        if (userData.user_type === 'fan') {
          navigate('/fan/dashboard', { replace: true });
        } else if (userData.user_type === 'admin') {
          navigate('/admin/dashboard', { replace: true });
        }
      }
    };

    checkAuth();
  }, [navigate]);

  // 첫 가입 시에는 기본 통계 (0 또는 빈 데이터)
  const stats = [
    { icon: 'ri-user-heart-line', label: '총 구독자', value: '0', color: 'from-pink-400 to-pink-600' },
    { icon: 'ri-money-dollar-circle-line', label: '이번 달 수익', value: '₩0', color: 'from-purple-400 to-purple-600' },
    { icon: 'ri-heart-fill', label: '총 좋아요', value: '0', color: 'from-blue-400 to-blue-600' },
    { icon: 'ri-eye-line', label: '총 조회수', value: '0', color: 'from-teal-400 to-teal-600' },
  ];

  // 첫 가입 시에는 빈 배열
  const recentSubscribers: any[] = [];
  const recentTips: any[] = [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-pink-50 to-purple-50">
      <CreatorSidebar onUploadClick={() => setShowUploadModal(true)} />
      
      <div className="lg:ml-64 p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* 헤더 */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">대시보드</h1>
            <p className="text-gray-600">안녕하세요, {currentUser?.username || currentUser?.full_name || '크리에이터'}님! 👋</p>
          </div>

          {/* 통계 카드 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-pink-100 shadow-lg hover:shadow-xl transition-all">
                <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mb-4`}>
                  <i className={`${stat.icon} text-white text-2xl`}></i>
                </div>
                <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
              </div>
            ))}
          </div>

          {/* 콘텐츠 업로드 CTA */}
          <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-400 rounded-3xl p-8 mb-8 text-white shadow-xl">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-2xl font-bold mb-2">새로운 콘텐츠를 공유하세요!</h2>
                <p className="text-white/90">팬들이 당신의 새로운 콘텐츠를 기다리고 있어요 ✨</p>
              </div>
              <button
                onClick={() => setShowUploadModal(true)}
                className="bg-white text-purple-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-upload-cloud-line mr-2"></i>
                콘텐츠 올리기
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* 최근 구독자 */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-pink-100 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">최근 구독자</h2>
                <i className="ri-user-heart-line text-pink-500 text-2xl"></i>
              </div>
              {recentSubscribers.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <i className="ri-user-heart-line text-3xl text-gray-400"></i>
                  </div>
                  <p className="text-gray-600 mb-2">아직 구독자가 없습니다</p>
                  <p className="text-sm text-gray-500">콘텐츠를 올려서 팬들을 모아보세요!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentSubscribers.map((subscriber) => (
                    <div key={subscriber.id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-pink-50 transition-all cursor-pointer">
                      <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                        <img
                          src={subscriber.avatar}
                          alt={subscriber.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800">{subscriber.name}</p>
                        <p className="text-sm text-gray-600">{subscriber.date}</p>
                      </div>
                      <span className="px-3 py-1 rounded-full bg-gradient-to-r from-pink-400 to-purple-400 text-white text-xs font-semibold">
                        레벨 {subscriber.level}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 최근 팁 */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-pink-100 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">최근 받은 팁</h2>
                <i className="ri-gift-line text-purple-500 text-2xl"></i>
              </div>
              {recentTips.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <i className="ri-gift-line text-3xl text-gray-400"></i>
                  </div>
                  <p className="text-gray-600 mb-2">아직 받은 팁이 없습니다</p>
                  <p className="text-sm text-gray-500">팬들이 응원을 보내면 여기에 표시됩니다!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentTips.map((tip) => (
                    <div key={tip.id} className="p-4 rounded-xl bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-100">
                      <div className="flex items-start justify-between mb-2">
                        <p className="font-semibold text-gray-800">{tip.from}</p>
                        <p className="text-lg font-bold text-purple-600">₩{tip.amount.toLocaleString()}</p>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{tip.message}</p>
                      <p className="text-xs text-gray-500">{tip.date}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 업로드 모달 */}
      {showUploadModal && <UploadModal onClose={() => setShowUploadModal(false)} />}
    </div>
  );
}
